import { useEffect, useState } from 'react';
import { useHFAlert } from '@hf/designSystem/v1';
import {
  omsSharedViewInputSchema,
  omsSharedViewListSchema,
  omsSharedViewSchema,
  type OmsCustomLayout,
  type OmsSharedView,
  type OmsSharedViewList,
} from '@hf/sharedContract';
import { GA, track } from '@core/utils/analytics';
import { useHttp } from '@core/utils/http';

function byName(left: OmsSharedView, right: OmsSharedView): number {
  return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' }) || left.id.localeCompare(right.id);
}

/** The company's saved Custom layouts. Anyone in the company can update or delete them. */
export function useSharedViews() {
  const { request } = useHttp();
  const { push } = useHFAlert();
  const [views, setViews] = useState<OmsSharedView[] | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void request<OmsSharedViewList>('/oms/shared-views?kind=custom', { responseSchema: omsSharedViewListSchema }).then((result) => {
      if (!cancelled) setViews(result.status === 200 && Array.isArray(result.data?.views) ? [...result.data.views].sort(byName) : []);
    });
    return () => {
      cancelled = true;
    };
  }, [request]);

  /** Creates a layout, or overwrites the one with `id`. Returns the saved layout, or null when the save failed. */
  const save = async (id: string | null, name: string, payload: OmsCustomLayout): Promise<OmsSharedView | null> => {
    setSaving(true);
    const result = await request<OmsSharedView>(`/oms/shared-views/custom/${id ?? crypto.randomUUID()}`, {
      method: 'PUT',
      json: { name, custom: payload },
      requestSchema: omsSharedViewInputSchema,
      responseSchema: omsSharedViewSchema,
    });
    setSaving(false);
    // Someone deleted this layout since it was listed. Drop it so the Custom layout falls back to New layout.
    if (result.status === 404 && id) setViews((current) => (current ?? []).filter((view) => view.id !== id));
    if ((result.status !== 200 && result.status !== 201) || !result.data) return null;
    const saved = result.data;
    setViews((current) => [...(current ?? []).filter((view) => view.id !== saved.id), saved].sort(byName));
    track(GA.savedOmsCustomLayout, { item_id: saved.id, layout: 'custom' });
    push({ severity: 'success', message: 'Layout saved' });
    return saved;
  };

  const remove = async (id: string): Promise<boolean> => {
    setSaving(true);
    const result = await request(`/oms/shared-views/custom/${id}`, { method: 'DELETE' });
    setSaving(false);
    if (result.status !== 204) return false;
    setViews((current) => (current ?? []).filter((view) => view.id !== id));
    track(GA.deletedOmsCustomLayout, { item_id: id, layout: 'custom' });
    push({ severity: 'success', message: 'Layout deleted' });
    return true;
  };

  return { views, saving, save, remove };
}
